import axios from 'axios';
import {
    CSRF_TOKEN_META_TAG_NAME, LOGIN_PATH, OAUTH_PATH, UNAUTHORIZED_PATH
} from '@constants/index';
import {isClient} from '@utils/isClient';
import {getIsSafeRedirectUrlProtocol} from '@utils/urlValidation';
import {refreshAccessToken, getIsTokenExpired} from './tokenManager';
import {getIsRecipient, getIsOnPublicRoute} from './userRole';

const ESL_API_VERSION = '11.47';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const TIMEOUT_MS = 60000; // 60 seconds
const BEARER_PREFIX = 'Bearer';

let httpClientInstance = null;
let refreshTokenPromise = null;
let appStore = null;

const getSessionExpiredUrl = (isRecipient) => {
    const state = appStore?.getState?.();
    const brand = isRecipient
        ? state?.signerUi?.defaultBrand
        : state?.brandSettings?.brand;
    return (isRecipient ? brand?.url_recipient_session_expired : brand?.url_user_session_expired) || '';
};

const redirectOnUnauthorized = (isRecipient) => {
    const brandSessionExpiredUrl = getSessionExpiredUrl(isRecipient);
    if (brandSessionExpiredUrl && getIsSafeRedirectUrlProtocol(brandSessionExpiredUrl)) {
        window.location.href = brandSessionExpiredUrl;
        return;
    }
    if (isRecipient) {
        window.location.href = `${UNAUTHORIZED_PATH}/recipient`;
        return;
    }
    const currentPath = window.location.pathname + window.location.search;
    const hasTarget = currentPath && currentPath !== LOGIN_PATH;
    window.location.href = hasTarget
        ? `${LOGIN_PATH}?target=${encodeURIComponent(currentPath)}`
        : LOGIN_PATH;
};

const shouldSkipOAuthHeaders = (url) => url.includes(OAUTH_PATH)
           || (getIsOnPublicRoute() && !url.includes('/proxy'));

export const setReduxStoreToHttpClient = (store) => {
    appStore = store;
};

const handleTokenRefresh = async () => {
    if (!appStore) {
        return null;
    }

    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    refreshTokenPromise = (async () => {
        try {
            const state = appStore.getState();
            const expiredAccessToken = state?.user?.accessToken ?? null;
            const tokenData = await refreshAccessToken(expiredAccessToken);
            if (tokenData?.accessToken && tokenData?.expiresAt) {
                appStore.dispatch({type: 'user/setAccessToken', payload: tokenData.accessToken});
                appStore.dispatch({type: 'user/setTokenExpiresAt', payload: tokenData.expiresAt});
            }
            return tokenData?.accessToken || null;
        } catch (error) {
            return null;
        } finally {
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
};

/** Uses `??` for timeout so a caller passing 0 (meaning "no timeout") is not silently overridden by `||`. */
const getRequestInterceptor = () => async (config) => {
    if (!appStore) return config;

    const state = appStore.getState();
    const requestUrl = config.url || '';
    const updatedConfig = {...config, headers: {...config.headers}};

    updatedConfig.timeout = config.timeout ?? state.app?.config?.HTTP_TIMEOUT_MS ?? TIMEOUT_MS;
    updatedConfig.metadata = {startTime: Date.now()};
    updatedConfig.headers.Accept = config.headers?.Accept || `application/json; esl-api-version=${ESL_API_VERSION}`;

    if (isClient) {
        const csrfTokenMeta = window.document.querySelector(`meta[name="${CSRF_TOKEN_META_TAG_NAME}"]`);
        const csrfToken = csrfTokenMeta ? csrfTokenMeta.getAttribute('content') : null;
        if (csrfToken) {
            updatedConfig.headers[CSRF_HEADER_NAME] = csrfToken;
        }
    }

    if (config.skipAuth || shouldSkipOAuthHeaders(requestUrl)) {
        return updatedConfig;
    }

    if (!isClient) {
        return updatedConfig;
    }

    const {accessToken, tokenExpiresAt} = state.user;

    if (accessToken && tokenExpiresAt) {
        if (isClient && getIsTokenExpired(tokenExpiresAt)) {
            const newAccessToken = await handleTokenRefresh();
            if (newAccessToken) {
                updatedConfig.headers.Authorization = `${BEARER_PREFIX} ${newAccessToken}`;
            } else if (!getIsOnPublicRoute()) {
                const isRecipient = getIsRecipient(accessToken);
                redirectOnUnauthorized(isRecipient);
                return updatedConfig;
            }
        } else {
            updatedConfig.headers.Authorization = `${BEARER_PREFIX} ${accessToken}`;
        }
    }

    return updatedConfig;
};

const getResponseInterceptor = () => ({
    fulfilled: (response) => response,
    rejected: async (error) => {
        const originalRequest = error.config;
        const requestUrl = originalRequest?.url || '';

        if (shouldSkipOAuthHeaders(requestUrl)) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest.isRetry && isClient) {
            originalRequest.isRetry = true;

            const newAccessToken = await handleTokenRefresh();
            if (newAccessToken) {
                originalRequest.headers.Authorization = `${BEARER_PREFIX} ${newAccessToken}`;

                return httpClientInstance(originalRequest);
            }

            if (!getIsOnPublicRoute()) {
                const accessToken = appStore?.getState?.()?.user?.accessToken;
                const isRecipient = getIsRecipient(accessToken);

                redirectOnUnauthorized(isRecipient);
            }
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
});

function getHttpClient() {
    if (!httpClientInstance) {
        httpClientInstance = axios.create({
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache'
            },
            timeout: TIMEOUT_MS,
            withCredentials: true
        });

        httpClientInstance.interceptors.request.use(getRequestInterceptor());

        const responseInterceptor = getResponseInterceptor();
        httpClientInstance.interceptors.response.use(
            responseInterceptor.fulfilled,
            responseInterceptor.rejected
        );
    }

    return httpClientInstance;
}

export default getHttpClient();
